import React from 'react';

import { useAccount } from '~/apis';
import { useTranslation } from '~/app';
import { selectGameType, useSelector } from '~/app/selectors';
import CountryFlagTooltip from '~/components/CountryFlagTooltip';
import ProfilePicture from '~/components/ProfilePicture';
import PlayerProfilePicture from '~/components/ProfilePicture/PlayerProfilePicture';
import { useProfileNavigation } from '~/profile/profileNavigation';
import { GameType } from '~/server/src/dtos';

interface HorseInfoProps {
  isPreview?: boolean;
  isDropped?: boolean;
  horseName: string;
  horseRealColor?: string;
  jockeyName?: string;
  horseTotalRank?: number;
  playerName: string;
  country?: string;
  handicap?: number;
  userId?: string;
}

/**
 * Content of tooltip while hovering / clicking horse icon
 */
const HorseInfo = ({
  isPreview,
  isDropped,
  playerName,
  country,
  horseName,
  horseRealColor,
  jockeyName,
  horseTotalRank,
  handicap,
  userId,
}: HorseInfoProps): JSX.Element => {
  const { t, locale } = useTranslation();
  const gameType = useSelector(selectGameType);
  const isLocalMode = gameType === GameType.Training;
  const { data: account } = useAccount();
  const { openUserProfile } = useProfileNavigation();

  const handlePlayerNameClick = () => {
    if (userId) {
      openUserProfile(userId, account?.userId);
    }
  };

  // Don't make bot usernames clickable
  const isBotUser = userId?.startsWith('bot_') || false;

  return (
    <div className="flex min-w-[280px] flex-col space-y-3 p-3 align-middle text-sm">
      {/* Player Avatar Section */}
      <div className="flex flex-row items-center space-x-3 rounded-lg bg-gray-50 p-2">
        <div className="shrink-0">
          {userId ? (
            <PlayerProfilePicture
              userId={userId}
              username={playerName}
              size="md"
              className="h-12 w-12"
            />
          ) : (
            <ProfilePicture
              username={playerName}
              size="md"
              className="h-12 w-12"
              userId={userId}
              isLocalMode={isLocalMode}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {userId ? (
            <button
              onClick={handlePlayerNameClick}
              disabled={isBotUser}
              className={`truncate text-left font-medium text-gray-900 transition-colors ${
                isBotUser
                  ? 'cursor-default text-gray-500'
                  : 'cursor-pointer text-primary hover:text-primary-dark hover:underline'
              }`}
            >
              {playerName}
            </button>
          ) : (
            <div className="truncate font-medium text-gray-900">{playerName}</div>
          )}
        </div>
      </div>

      {/* Nationality Section */}
      {country && (
        <div className="flex flex-row items-center space-x-2 rounded-lg bg-blue-50 p-2">
          <CountryFlagTooltip countryCode={country} size="lg" showTooltip={false} />
          <span className="text-sm font-medium text-blue-900">
            {/* Get country name using browser's Intl API */}
            {(() => {
              try {
                const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
                return displayNames.of(country) || country;
              } catch {
                return country;
              }
            })()}
          </span>
        </div>
      )}

      {/* Horse Information Section */}
      <div className="flex flex-col space-y-2">
        {jockeyName && (
          <span className="text-xs font-light text-gray-600">
            {t('horseWithJockeyLabel', { placeholders: { jockeyName } })}
          </span>
        )}
        <span className="text-base font-semibold text-gray-900">{horseName}</span>
      </div>

      {horseRealColor && (
        <div className="flex flex-row items-center rounded-lg bg-gray-50 p-2">
          <span className="mr-2 text-xs text-gray-600">{t('game_playerInfoOrgColor')}</span>
          <div
            className="h-4 w-4 rounded-full border-2 border-white shadow-xs"
            style={{ backgroundColor: horseRealColor }}
          />
        </div>
      )}

      <div className="h-px w-full rounded-sm bg-gray-200" />

      {isPreview ? (
        <div className="text-sm text-gray-600 italic">{t('game_playerInfoWaiting')}</div>
      ) : (
        <div className="space-y-1">
          {horseTotalRank != null && (
            <div className="text-sm text-gray-700">
              {t('game_playerInfoRankTotal', { placeholders: { totalRank: horseTotalRank + 1 } })}
            </div>
          )}

          {!isDropped ? (
            <div className="text-sm text-gray-700">
              {handicap
                ? t('game_playerInfoHandicap', { placeholders: { handicap: handicap + 1 } })
                : t('game_playerInfoNoHandicap')}
            </div>
          ) : (
            <>
              <div className="my-2 h-px w-full rounded-sm bg-gray-200" />
              <div className="font-bold text-red-600">{t('game_playerInfoDropped')}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HorseInfo;
